import { ProductCatalogEmbroideryTypes } from '../../../__generated__/graphql/catalog/graphql'
import { EmbroiderableProductEmbroideryConfig } from '../../../types/graphql/product'

type ProductEmbroideryConfig = EmbroiderableProductEmbroideryConfig | null | undefined

/**
 * Which embroidery types a product supports, keyed by type.
 *
 * A type counts as supported only when its option is truthy. The catalog query asks for
 * text, icon and logo unconditionally, so an unsupported one comes back as a present key
 * with a null value — testing for the key alone would call every product embroiderable.
 *
 * @param productEmbroideryConfig - The variant group's embroidery config, if it has one.
 */
export const getEmbroideryTypeSupport = ({
  productEmbroideryConfig,
}: {
  productEmbroideryConfig: ProductEmbroideryConfig
}): Record<ProductCatalogEmbroideryTypes, boolean> => ({
  TEXT: !!productEmbroideryConfig?.options.text,
  ICON: !!productEmbroideryConfig?.options.icon,
  LOGO: !!productEmbroideryConfig?.options.logo,
})

/**
 * Whether a product can be embroidered at all — true when it supports at least one type.
 *
 * Pages read this through the embroidery provider rather than calling it on the variant group
 * themselves, because a flow like gifting hands the provider a narrowed config: the catalog can
 * offer embroidery the gifter turned off, or lack a logo option the gifter's logo stubs in.
 *
 * @param productEmbroideryConfig - The config the embroidery provider was given, if any.
 */
export const isProductEmbroiderable = ({
  productEmbroideryConfig,
}: {
  productEmbroideryConfig: ProductEmbroideryConfig
}): boolean => Object.values(getEmbroideryTypeSupport({ productEmbroideryConfig })).some(Boolean)
